import React from 'react';
import { tokens } from '@servicetitan/tokens/core';

import {
	AnvilSelect,
	Button,
	ButtonToggle,
	ColorPicker,
	DatePicker,
	DateRangePicker,
	Input,
	Modal,
	Drawer,
	Dialog,
	Popover,
	Sidebar,
	SideNav,
	Takeover,
	TabGroup,
	Tab,
	TimePicker,
} from '../../../components';
import { Portal } from '../../../internal';
import { useFocusTrap } from '../../../hooks';
import {
	Menu,
	MenuItem,
} from '../../../components/ActionMenu/ActionMenu.stories';

export const DatePickerWithValue = () => {
	const [date, setDate] = React.useState(new Date('11/11/2022'));

	return (
		<div className="m-2">
			<DatePicker value={date} onChange={(v) => setDate(v as any)} />
		</div>
	);
};

export const DateRangePickerWithValue = () => {
	const [range, setRange] = React.useState({
		start: new Date('11/07/2022'),
		end: new Date('11/15/2022'),
	});

	return (
		<div className="m-2">
			<DateRangePicker
				value={range}
				minDate={new Date('01/01/2022')}
				maxDate={new Date('12/31/2022')}
				onChange={(v: any) => setRange(v)}
			/>
		</div>
	);
};

export const TimePickerWithValue = () => {
	const [time, setTime] = React.useState<any>('08:00 AM');

	return (
		<div className="m-2">
			<TimePicker
				min="00:00"
				max="05:00 PM"
				value={time}
				onChange={setTime}
			/>
		</div>
	);
};

const dummyOptions = [
	{
		text: 'Jane Doe',
		value: 1,
		disabled: true,
	},
	{
		text: 'Bob Ross',
		value: 2,
	},
	{
		text: 'Jackie Robinson',
		value: 3,
	},
	{
		text: 'Alexandria Garcia',
		value: 4,
	},
];

export const SelectWithValue = () => {
	return (
		<div className="m-2">
			<AnvilSelect
				options={dummyOptions}
				trigger={{ placeholder: 'Select a technician' }}
			/>
		</div>
	);
};

export const FocusTrapTest = () => {
	const sharded = React.useRef();
	const ref = useFocusTrap({ shards: [sharded] });

	return (
		<React.Fragment>
			<div ref={ref}>
				<Input placeholder="Inside 1" />
				<br />
				<Input placeholder="Inside 2" />
			</div>
			<Portal>
				<div ref={sharded}>
					<Input placeholder="Inside 3 (shard)" />
				</div>
			</Portal>
		</React.Fragment>
	);
};

export const ModalTest = () => {
	const [open, setOpen] = React.useState(false);

	return (
		<div>
			<Button onClick={() => setOpen(!open)}>Open</Button>
			<br />
			<Modal
				open={open}
				portal
				closable
				onClose={() => setOpen(!open)}
				title="Modal"
			>
				<Input placeholder="Inside 1" />
				<br />
				<Input placeholder="Inside 2" />
			</Modal>
		</div>
	);
};

export const DrawerTest = () => {
	const [open, setOpen] = React.useState(false);

	return (
		<div>
			<Button onClick={() => setOpen(!open)}>Open</Button>
			<br />
			<Drawer
				open={open}
				header="Drawer"
				portal
				backdrop
				onClose={() => setOpen(!open)}
			>
				<Input placeholder="Inside 1" />
				<br />
				<Input placeholder="Inside 2" />
			</Drawer>
		</div>
	);
};

export const DialogTest = () => {
	const [open, setOpen] = React.useState(false);

	return (
		<div>
			<Button onClick={() => setOpen(!open)}>Open</Button>
			<br />
			<Dialog
				open={open}
				primaryActionName="Close"
				onPrimaryActionClick={() => setOpen(!open)}
				title="Dialog"
			>
				<Input placeholder="Inside 1" />
				<br />
				<Input placeholder="Inside 2" />
			</Dialog>
		</div>
	);
};

export const PopoverTest = () => {
	const [open, setOpen] = React.useState(false);

	return (
		<div>
			<Popover
				open={open}
				trigger={<Button onClick={() => setOpen(!open)}>Open</Button>}
				direction="br"
				portal
				focusTrapOptions={{ disabled: !open, autoFocus: true }}
				onClickOutside={() => setOpen(!open)}
			>
				<Input placeholder="Inside 1" />
				<br />
				<Input placeholder="Inside 2" />
				<br />
				<Button onClick={() => setOpen(!open)}>Close</Button>
			</Popover>
		</div>
	);
};

export const TakeoverTest = () => {
	const [open, setOpen] = React.useState(false);

	return (
		<div>
			<Button onClick={() => setOpen(!open)}>Open</Button>
			<br />
			<Takeover
				open={open}
				title="Takeover"
				onClose={() => setOpen(!open)}
			>
				<Input placeholder="Inside 1" />
				<br />
				<Input placeholder="Inside 2" />
			</Takeover>
		</div>
	);
};

export const ActionMenuTest = () => (
	<Menu
		direction="br"
		trigger={(props: any) => <Button {...props}>Open</Button>}
	>
		<MenuItem>Item 1</MenuItem>
		<Menu
			direction="rb"
			trigger={(props: any) => (
				<MenuItem {...props} cascading>
					Item 2 (cascading)
				</MenuItem>
			)}
		>
			<MenuItem>Item 21</MenuItem>
			<Menu
				direction="rb"
				trigger={(props: any) => (
					<MenuItem {...props} cascading>
						Item 22 (cascading)
					</MenuItem>
				)}
			>
				<MenuItem>Item 31</MenuItem>
				<MenuItem>Item 32</MenuItem>
				<MenuItem>Item 33</MenuItem>
			</Menu>
			<MenuItem>Item 23</MenuItem>
			<MenuItem>Item 24</MenuItem>
		</Menu>
		<MenuItem>Item 3</MenuItem>
		<MenuItem>Item 4</MenuItem>
	</Menu>
);

const query = (s: string) => document.querySelectorAll(s);

export const SideNavTest = () => {
	const handleAction = () => setCount(count + 1);
	const handleClick = (index: number) => () => setActive(index);
	const [active, setActive] = React.useState<number>(21);
	const [count, setCount] = React.useState<number>(0);

	/*
	 * Setting attribute hidden on invisible items for proper jest testing, due to jest's DOM
	 * has no proper offsetWidth and offsetHeight support, which are used to detect visibility.
	 */
	React.useEffect(() => {
		const all = [...query('.SideNavItem__button')];
		const hidden = [...query('.Collapsible--hidden .SideNavItem__button')];
		all.forEach((el: any) => (el.hidden = false));
		hidden.forEach((el: any) => (el.hidden = true));
	}, [count]);

	return (
		<Sidebar localStorageKey="test">
			<SideNav onKeyDown={handleAction} onClick={handleAction}>
				<SideNav.Item onClick={handleClick(1)} active={active === 1}>
					Item 1
				</SideNav.Item>
				<SideNav.Item
					active={[21, 22, 23].includes(active)}
					collapsible={{ expanded: true }}
				>
					Item 2
					<SideNav>
						<SideNav.Item
							onClick={handleClick(21)}
							active={active === 21}
						>
							Item 21
						</SideNav.Item>
						<SideNav.Item
							onClick={handleClick(22)}
							active={active === 22}
						>
							Item 22
						</SideNav.Item>
						<SideNav.Item
							onClick={handleClick(23)}
							active={active === 23}
						>
							Item 23
						</SideNav.Item>
					</SideNav>
				</SideNav.Item>
				<SideNav.Item onClick={handleClick(3)} active={active === 3}>
					Item 3
				</SideNav.Item>
				<SideNav.Item onClick={handleClick(4)} active={active === 4}>
					Item 4
				</SideNav.Item>
			</SideNav>
		</Sidebar>
	);
};

export const TabTest = () => {
	const [active, setActive] = React.useState<number>(1);
	const click = (index: number) => () => setActive(index);

	return (
		<div style={{ margin: '10rem 0' }}>
			<TabGroup
				divider
				action={
					<Button size="xsmall" primary outline>
						Action
					</Button>
				}
			>
				<Tab onClick={click(0)} active={active === 0}>
					Tab 1
				</Tab>
				<Tab onClick={click(1)} active={active === 1}>
					Tab 2
				</Tab>
				<Tab onClick={click(2)} active={active === 2}>
					Tab 3
				</Tab>
				<Tab disabled onClick={click(3)} active={active === 3}>
					Tab 4
				</Tab>
			</TabGroup>
		</div>
	);
};

export const ButtonToggleTest = () => {
	const [state, setState] = React.useState(2);

	const onChangeHandler = (value: any) => {
		setState(value);
	};

	return (
		<div style={{ margin: '10rem 0' }}>
			<ButtonToggle
				value={state}
				small
				clearable
				onChange={onChangeHandler}
				options={[
					{
						text: 'Item 1',
						value: 1,
					},
					{
						text: 'Item 2',
						value: 2,
					},
					{
						text: 'Item 3',
						value: 3,
					},
					{
						text: 'Item 4',
						value: 4,
						disabled: true,
					},
				]}
			/>
		</div>
	);
};

export const ColorPickerTest = () => {
	const colors = [
		tokens.colorBlue600,
		tokens.colorRed200,
		'#1fbc70',
		'#ff745f',
		'#faafe2',
		'#8772e5',
		'#01f346',
		'#90efc0',
		'#ffcc80',
		'rgb(8, 191, 223)',
		'rgb(204, 157, 127)',
	];

	const [color, setState] = React.useState<string>(colors[3]);
	const onChangeFunction = (newColor: string) => setState(newColor);

	return (
		<div style={{ margin: '10rem 0' }}>
			<ColorPicker
				colors={colors}
				onChange={onChangeFunction}
				tabIndex={-1}
				value={color}
			/>
		</div>
	);
};
